<template>
    <div>
        <p class="slds-p-bottom_small">
            Please read our <span class="slds-text-link">
                <a
                    href="https://help.salesforce.com/s/articleView?id=000355944&type=1"
                    target="_blank"
                >formatting requirements</a>
            </span>
            prior to uploading
        </p>
        <div
            v-if="(!store__dataset.filename && !store__dataset.showFileUploadSuccess)"
            class="slds-form-element file-reader"
        >
            <div class="slds-col">
                <div class="slds-form-element__control">
                    <div class="slds-file-selector slds-file-selector_files">
                        <div class="slds-file-selector__dropzone">
                            <input
                                id="file-upload-input-01"
                                ref="fileUpload"
                                class="slds-file-selector__input slds-assistive-text"
                                accept="text/csv"
                                aria-labelledby="file-selector-primary-label file-selector-secondary-label"
                                type="file"
                                @change="loadTextFromFile"
                            >
                            <label
                                id="file-selector-secondary-label"
                                class="slds-file-selector__body"
                                for="file-upload-input-01"
                            >
                                <span class="slds-file-selector__button slds-button slds-button_neutral">
                                    <span class="slds-button__icon ma-icon ma-icon-upload"></span>Upload Files</span>
                            </label>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div
            v-if="(store__dataset.filename && store__dataset.showFileUploadSuccess)"
            class="slds-m-top_small slds-medium-size_3-of-4 slds-box slds-grid slds-grid_vertical-align-center"
        >
            <MAIcon
                src="~icons/doctype/csv.svg"
                :inheritFillFromParent="false"
                :iconClass="['slds-icon', 'slds-input__icon', 'slds-input__icon_left', 'slds-icon-text-default']"
            />
            <span class="slds-m-horizontal_x-small">
                {{ store__dataset.filename }}
            </span>
            <div
                class="slds-progress-bar slds-progress-bar_circular slds-m-horizontal_x-small"
                aria-valuemin="0"
                aria-valuemax="100"
                aria-valuenow="100"
                role="progressbar"
            >
                <span
                    class="slds-progress-bar__value slds-progress-bar_large"
                    style="width: 100%;"
                >
                    <span class="slds-assistive-text">Progress: 100%</span>
                </span>
            </div>
            <span class="ma-icon ma-icon-success ma-icon-medium slds-m-right_x-small"></span>
            <div>
                <button
                    class="slds-button slds-m-left_large"
                    @click="removeFile()"
                >
                    Remove
                </button>
            </div>
        </div>
    </div>
</template>

<script>

import { mapState, mapMutations } from 'vuex';
import Papa from 'papaparse';

export default {
    name: 'FileUploader',
    data() {
        return {
            file: null
        };
    },
    computed: {
        ...mapState({
            store__papaParseConfig: 'papaParseConfig',
            store__dataset: 'dataset'
        })
    },
    methods: {
        ...mapMutations({
            store__showSpatialReferencePicker: 'showSpatialReferencePicker',
            store__toggleUploadSpinner: 'toggleUploadSpinner',
            store__toggleLockButtons: 'toggleLockButtons',
            store__resetData: 'resetData'
        }),
        /* Remove file from browser */
        removeFile() {
            this.store__resetData();
            if (this.$refs.fileUpload) this.$refs.fileUpload.value = '';
        },
        /* Loads file contents using FileReader API */
        loadTextFromFile(ev) {
            try {
                // Check size of file, read if file size is less than 10 MB.
                if (ev.target.files[0] && ev.target.files[0].size > 10000000) {
                    const toastMessage = {
                        showCloseButton: true,
                        type: 'error',
                        title: 'Upload Error',
                        body: 'File size exceeded. Max file size 10MB.',
                        timeout: 5000
                    };

                    this.$vueOnToast.pop(toastMessage);
                } else {
                    this.store__showSpatialReferencePicker(false);

                    const reader = new FileReader();

                    // eslint-disable-next-line prefer-destructuring
                    this.file = ev.target.files[0];

                    // Begin loading spinner...
                    this.store__toggleUploadSpinner();

                    reader.onload = (e) => {
                        this.store__dataset.contents = e.target.result;

                        if (this.parseCsv()) { // Check if CSV has content...
                            this.submitFile()
                            .then((res) => {
                                this.store__dataset.datasetID = res.data.datasetID;
                                this.store__showSpatialReferencePicker(true);
                            })
                            .catch((err) => {
                                throw err;
                            })
                            .finally(() => {
                                // End loading spinner...
                                this.store__toggleUploadSpinner();
                            });
                        } else {
                            // End loading spinner...
                            this.store__toggleUploadSpinner();
                        }
                    };
                    reader.onerror = (err) => {
                        throw err;
                    };
                    reader.readAsText(this.file);
                }
            } catch (err) {
                throw err;
            }
        },
        /* Reads CSV, stores the parsed data and headers */
        parseCsv() {
            try {
                const parsedData = Papa.parse(this.store__dataset.contents, this.store__papaParseConfig);

                if (parsedData.data.length === 0) {
                    const toastMessage = {
                        showCloseButton: true,
                        type: 'error',
                        title: 'Upload Error',
                        body: 'File is empty.',
                        timeout: 5000
                    };

                    this.$vueOnToast.pop(toastMessage);

                    return false; // Return false to prevent uploading...
                }

                this.store__dataset.filename = this.file.name;
                this.store__dataset.parsedData = parsedData;
                this.store__dataset.headers = parsedData.meta.fields;

                return true;
            } catch (e) {
                throw e;
            }
        },
        /* Submits the file to the express server */
        submitFile() {
            return new Promise((resolve, reject) => {
                const API_URL = document.location.href;

                const headers = {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                        'x-creator': this.store__dataset.creator
                    }
                };

                const formData = new FormData();
                formData.append('file', this.file);

                // Lock modal buttons...
                this.store__toggleLockButtons();

                this.$http.post(`${API_URL}api/dataset/upload`, formData, headers)
                .then((res) => {
                    if (res.status === 200) {
                        const toastMessage = {
                            showCloseButton: true,
                            type: 'success',
                            title: 'Upload Success',
                            body: `"${this.store__dataset.filename}" uploaded to server.`,
                            timeout: 5000
                        };

                        this.$vueOnToast.pop(toastMessage);
                        this.store__dataset.showFileUploadSuccess = true;
                        resolve(res);
                    } else {
                        const toastMessage = {
                            showCloseButton: true,
                            type: 'error',
                            title: 'Upload Error',
                            body: `There was an issue uploading "${this.store__dataset.filename}" to the server.`,
                            timeout: 5000
                        };

                        this.$vueOnToast.pop(toastMessage);
                        this.store__dataset.showFileUploadSuccess = false;
                        this.store__dataset.filename = null;
                        reject(res);
                    }
                })
                .catch((err) => {
                    const toastMessage = {
                        showCloseButton: true,
                        type: 'error',
                        title: 'Server Error',
                        body: 'An error has ocurred while contacting the server to begin your file upload.',
                        timeout: 5000
                    };

                    this.$vueOnToast.pop(toastMessage);
                    this.store__dataset.showFileUploadSuccess = false;
                    this.store__dataset.filename = null;
                    reject(err);
                })
                .finally(() => {
                    // Unlock buttons...
                    this.store__toggleLockButtons();
                });
            });
        }
    }
};
</script>

<style lang="scss" scoped>
    #dashboard-modal /deep/ .slds-modal__container {
        .slds-modal__content {
            height: 100%;
        }
    }

    .file-reader {
        color: #2c3e50;
    }

    .slds-pill__icon_container .slds-icon {
        display: flex;
        justify-content: center;
    }

    .slds-setup-assistant__item {
        border-bottom: none;
    }

    .dashboard-modal {
        min-height: 70%;
    }

    .hide-close /deep/ header button {
        display: none
    }

    .ma-icon-page {
        font-size: 2.5rem;
    }

    .ma-icon-success, .ma-icon-page {
        color: #4bca81;
        display: flex;
    }

    .slds-progress-bar--large, .slds-progress-bar_large {
        height: .75rem;
    }
</style>
